/* eslint-disable react/prop-types */
import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, Upload, FileText, Check, X, Sparkles, Users } from 'lucide-react';

/**
 * Phase 5.3 — Bulk legacy lead CSV import with Soft Match Preview™.
 *
 * Calm flow:
 *   Upload → validate → normalize → soft match → preview
 *   → choose merge / skip / keep both → confirm → audit log.
 *
 * Never bulk-dump. Never auto-merge. The importer always confirms each row.
 */
export default function GrowthImportPage() {
  const [step, setStep] = useState('upload');     // upload | preview | done
  const [csvText, setCsvText] = useState('');
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null);
  const [decisions, setDecisions] = useState({}); // row_no → {action, target_lead_id?}
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = e => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = ev => setCsvText(ev.target.result);
    reader.readAsText(f);
  };

  const runDryRun = async () => {
    if (!csvText.trim()) return setError('Paste a CSV or choose a file first.');
    setLoading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('csv_text', csvText);
      const r = await axios.post(`${API}/terra-growth/import/dry-run`, fd);
      setPreview(r.data);
      // Seed decisions from suggested_action.
      const seed = {};
      r.data.rows.forEach(row => {
        seed[row.row_no] = {
          action: row.suggested_action === 'review' ? 'skip' :
                   row.suggested_action,
          target_lead_id: row.soft_matches?.[0]?.lead_id || null,
        };
      });
      setDecisions(seed);
      setStep('preview');
    } catch (e) {
      setError(e.response?.data?.detail || 'Dry-run failed.');
    } finally { setLoading(false); }
  };

  const commit = async () => {
    setLoading(true); setError(null);
    try {
      const payload = {
        import_id: preview.import_id,
        decisions: Object.entries(decisions).map(([row_no, d]) => ({
          row_no: Number(row_no), ...d,
        })),
      };
      const r = await axios.post(`${API}/terra-growth/import/commit`, payload);
      setAudit(r.data.audit);
      setStep('done');
    } catch (e) {
      setError(e.response?.data?.detail || 'Commit failed.');
    } finally { setLoading(false); }
  };

  const reset = () => {
    setStep('upload'); setCsvText(''); setFileName(''); setPreview(null);
    setDecisions({}); setAudit(null); setError(null);
  };

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="growth-import-page" className="terra-page-pad max-w-6xl mx-auto px-8 py-12">
        <Header step={step} />
        {error && (
          <div data-testid="import-error"
               className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 text-[13px] text-amber-900 mb-6">
            {error}
          </div>
        )}
        {step === 'upload' && (
          <UploadStep csvText={csvText} setCsvText={setCsvText}
                      fileName={fileName} fileInputRef={fileInputRef}
                      handleFile={handleFile}
                      onContinue={runDryRun} loading={loading} />
        )}
        {step === 'preview' && preview && (
          <PreviewStep preview={preview} decisions={decisions}
                       setDecisions={setDecisions}
                       onBack={reset} onCommit={commit} loading={loading} />
        )}
        {step === 'done' && audit && (
          <DoneStep audit={audit} onReset={reset} />
        )}
      </div>
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Header
// ═══════════════════════════════════════════════════════════════════
function Header({ step }) {
  return (
    <header className="mb-10 max-w-3xl space-y-3">
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#8C6239] flex items-center gap-2">
        <Users className="w-3 h-3" /> Terra Growth OS™ · Onboarding
      </p>
      <h1 className="text-4xl font-['Outfit'] font-light text-[#2C2A29] tracking-tight">
        Growth Import
      </h1>
      <p className="text-sm text-[#8A8681] font-['Work_Sans'] leading-relaxed">
        Onboarding legacy leads is an ecosystem-greeting, never a data dump.
        Upload a CSV, listen to Soft Match Preview™, decide merge / skip /
        keep both per row, then confirm. Each row is treated with care.
      </p>
      <Stepper step={step} />
    </header>
  );
}

function Stepper({ step }) {
  const steps = [
    { key: 'upload',  label: 'Upload' },
    { key: 'preview', label: 'Soft Match Preview™' },
    { key: 'done',    label: 'Audit log' },
  ];
  return (
    <ol className="flex flex-wrap items-center gap-3 text-[11px] mt-4">
      {steps.map((s, i) => {
        const active = s.key === step;
        const done = ['preview','done'].indexOf(step) > i ||
                      (step === 'done' && i < 2);
        return (
          <li key={s.key} data-testid={`stepper-${s.key}`}
              className="inline-flex items-center gap-2">
            <span className={`inline-flex w-5 h-5 items-center justify-center rounded-full border text-[10px] tabular-nums ${
              active ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]' :
              done   ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                       'bg-white text-[#8A8681] border-[#D8D4CE]'}`}>
              {done ? <Check className="w-3 h-3" /> : i + 1}
            </span>
            <span className={active ? 'text-[#2C2A29]' : 'text-[#8A8681]'}>
              {s.label}
            </span>
            {i < steps.length - 1 && <span className="text-[#D8D4CE]">·</span>}
          </li>
        );
      })}
    </ol>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 1 — Upload
// ═══════════════════════════════════════════════════════════════════
function UploadStep({ csvText, setCsvText, fileName, fileInputRef,
                      handleFile, onContinue, loading }) {
  return (
    <section className="space-y-6">
      <article className="rounded-2xl border border-[#EBE8E3] bg-white p-6 space-y-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681]">
          1. Choose your source
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input ref={fileInputRef} type="file" accept=".csv,text/csv"
                 onChange={handleFile} className="hidden"
                 data-testid="csv-file-input" />
          <button data-testid="csv-file-button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 text-[12px] rounded-full px-4 py-2 border border-[#D8D4CE] hover:border-[#4A5D4E] text-[#2C2A29] transition">
            <Upload className="w-3.5 h-3.5" /> Upload CSV file
          </button>
          {fileName && (
            <span data-testid="csv-file-name"
                  className="inline-flex items-center gap-1.5 text-[11px] text-[#8A8681]">
              <FileText className="w-3 h-3" /> {fileName}
            </span>
          )}
        </div>
        <details className="rounded-xl bg-[#FBFaf9] p-3 text-[11px] text-[#5C5955]">
          <summary className="cursor-pointer text-[#2C2A29]">
            …or paste CSV text directly
          </summary>
          <textarea data-testid="csv-text-area"
                    value={csvText} onChange={e => setCsvText(e.target.value)}
                    rows={8}
                    placeholder="name,phone,email,city,centre,ecosystem,source_note,referred_by,current_stage,perception_state,notes"
                    className="w-full mt-2 rounded-md border border-[#EBE8E3] bg-white p-3 text-[12px] font-mono text-[#2C2A29] focus:outline-none focus:border-[#4A5D4E]" />
        </details>
      </article>

      <article className="rounded-2xl border border-[#E5D6BE] bg-[#FAF1E1]/40 p-5">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#8C6239] mb-2">
          Expected columns
        </p>
        <p className="text-[12px] text-[#5C3D1F] font-mono leading-relaxed">
          name · phone · email · city · centre · ecosystem · source_note ·
          referred_by · current_stage · perception_state · notes
        </p>
        <p className="text-[11px] text-[#8C6239] italic mt-3">
          Only `name` is required. Missing fields stay empty — never blocked.
        </p>
      </article>

      <button data-testid="csv-dry-run-btn"
              disabled={loading || !csvText.trim()}
              onClick={onContinue}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[12px] bg-[#2C2A29] text-white hover:bg-[#4A5D4E] disabled:opacity-40 disabled:cursor-not-allowed transition">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        Soft Match Preview™
      </button>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 2 — Preview
// ═══════════════════════════════════════════════════════════════════
function PreviewStep({ preview, decisions, setDecisions,
                       onBack, onCommit, loading }) {
  const totals = useMemo(() => {
    const t = { import: 0, merge: 0, skip: 0, keep_both: 0 };
    Object.values(decisions).forEach(d => { if (t[d.action] !== undefined) t[d.action]++; });
    return t;
  }, [decisions]);

  const setAction = (row_no, action) => {
    setDecisions(d => ({ ...d, [row_no]: { ...(d[row_no] || {}), action } }));
  };

  return (
    <section data-testid="preview-section" className="space-y-6">
      <Totals preview={preview} totals={totals} />
      <ul className="space-y-3">
        {preview.rows.map(row => (
          <RowCard key={row.row_no} row={row}
                   decision={decisions[row.row_no]}
                   onAction={a => setAction(row.row_no, a)} />
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#F4F2EE]">
        <button data-testid="commit-btn" onClick={onCommit}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[12px] bg-[#4A5D4E] text-white hover:bg-[#3A4D3E] disabled:opacity-40 transition">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Confirm onboarding
        </button>
        <button data-testid="back-btn" onClick={onBack}
                className="text-[11px] text-[#8A8681] hover:text-[#2C2A29] underline-offset-4 hover:underline">
          Discard and start over
        </button>
        <span className="text-[11px] text-[#8A8681] italic ml-auto">
          {preview.philosophy_note}
        </span>
      </div>
    </section>
  );
}

function Totals({ preview, totals }) {
  return (
    <div data-testid="preview-totals"
         className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <Tile label="Rows total" value={preview.rows_total} />
      <Tile label="Import"     value={totals.import} testId="total-import" />
      <Tile label="Merge"      value={totals.merge}  testId="total-merge" />
      <Tile label="Keep both"  value={totals.keep_both} testId="total-keep-both" />
      <Tile label="Skip"       value={totals.skip}   testId="total-skip" />
    </div>
  );
}
function Tile({ label, value, testId }) {
  return (
    <article data-testid={testId} className="rounded-2xl border border-[#EBE8E3] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">{label}</p>
      <p className="text-2xl font-['Outfit'] tabular-nums text-[#2C2A29] mt-1">{value}</p>
    </article>
  );
}

function RowCard({ row, decision, onAction }) {
  const action = decision?.action || 'import';
  const norm = row.normalized;
  const errs = row.validation_errors || [];
  const matches = row.soft_matches || [];
  const hasMatch = matches.length > 0;

  return (
    <li data-testid={`row-${row.row_no}`}
        className={`rounded-2xl border bg-white p-5 ${
          row.suggested_action === 'review' ? 'border-[#E5D6BE]' :
          'border-[#EBE8E3]'}`}>
      <header className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
        <div className="flex items-baseline gap-3 min-w-0">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] tabular-nums">
            row {row.row_no}
          </span>
          <span className="text-base text-[#2C2A29] truncate">
            {norm.name || <em className="text-[#B5B0A8]">(no name)</em>}
          </span>
          <span className="text-[11px] text-[#8A8681]">
            {[norm.phone, norm.email, norm.city].filter(Boolean).join(' · ') || '—'}
          </span>
        </div>
        <SuggestedTag suggested={row.suggested_action} />
      </header>

      {errs.length > 0 && (
        <div data-testid={`row-errors-${row.row_no}`}
             className="text-[11px] text-amber-800 mb-3">
          Validation: {errs.join(', ')}
        </div>
      )}

      {hasMatch && (
        <div data-testid={`row-matches-${row.row_no}`}
             className="rounded-xl bg-[#FBFaf9] border border-[#EBE8E3] p-3 mb-3">
          <p className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681] mb-2">
            Soft match candidates
          </p>
          <ul className="space-y-1">
            {matches.map(m => (
              <li key={m.lead_id} className="flex items-center justify-between text-[12px]">
                <span className="truncate text-[#2C2A29]">{m.name}</span>
                <span className="text-[11px] text-[#8A8681] tabular-nums">
                  score {m.score} · {m.reasons.join(', ')}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {['import', 'merge', 'keep_both', 'skip'].map(a => (
          <button key={a}
                  data-testid={`row-${row.row_no}-action-${a}`}
                  onClick={() => onAction(a)}
                  disabled={a === 'merge' && !hasMatch}
                  className={`text-[11px] rounded-full px-3 py-1.5 border transition ${
                    action === a
                      ? 'bg-[#4A5D4E] text-white border-[#4A5D4E]'
                      : 'bg-white text-[#5C5955] border-[#D8D4CE] hover:border-[#B5B0A8] disabled:opacity-30 disabled:cursor-not-allowed'
                  }`}>
            {a.replace('_', ' ')}
          </button>
        ))}
      </div>
    </li>
  );
}

const SUGGEST_TONE = {
  merge:  'border-emerald-200 bg-emerald-50/50 text-emerald-800',
  review: 'border-amber-200 bg-amber-50/50 text-amber-800',
  import: 'border-stone-200 bg-stone-50/50 text-stone-700',
};
function SuggestedTag({ suggested }) {
  const tone = SUGGEST_TONE[suggested] || SUGGEST_TONE.import;
  return (
    <span className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border ${tone}`}>
      suggested · {suggested}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Step 3 — Done (audit summary)
// ═══════════════════════════════════════════════════════════════════
function DoneStep({ audit, onReset }) {
  return (
    <section data-testid="audit-summary" className="space-y-6">
      <article className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6">
        <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-800 mb-1 flex items-center gap-1">
          <Check className="w-3 h-3" /> Onboarding complete
        </p>
        <p className="text-base text-[#2C2A29]">
          {audit.rows_imported + audit.rows_merged + audit.rows_kept_both} of {audit.rows_total} rows
          calmly onboarded.
        </p>
      </article>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Imported"   value={audit.rows_imported}   testId="audit-imported" />
        <Tile label="Merged"     value={audit.rows_merged}     testId="audit-merged" />
        <Tile label="Kept both"  value={audit.rows_kept_both}  testId="audit-kept-both" />
        <Tile label="Skipped"    value={audit.rows_skipped}    testId="audit-skipped" />
        <Tile label="Validation" value={audit.validation_errors} testId="audit-validation" />
      </div>
      <p className="text-[11px] text-[#8A8681] italic">
        Audit log ID <span className="font-mono">{audit.import_id}</span> — stored in
        terra_growth_import_audits, available via /import/history.
      </p>
      <button data-testid="reset-btn" onClick={onReset}
              className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[12px] bg-white text-[#2C2A29] border border-[#D8D4CE] hover:border-[#4A5D4E] transition">
        <X className="w-4 h-4" /> Greet another batch
      </button>
    </section>
  );
}
